import React, { useCallback, useEffect, useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { ThinPlusInCircle } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { ActionRoleGuard } from "components/guards/Action-role.guard";
import { Task } from "components/Task-manage";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useDidUpdate, useNavigationHooks } from "hooks";
import { useClientActions, useCreateTaskSelect, useTaskManageActions } from "store";
import type { TaskStatus } from "store/types";
import type { IClientTaskFilters } from "store/types/client";

import { RoleName } from "../../../constants";

const colorsStatuses: Record<TaskStatus, string> = {
  open: "#5d7782",
  "in-progress": "#b2d6f8",
  done: "#b2eaad"
};

interface Props {
  clientId: number;
}

/**
 * Tasks widget on the client overview — self-contained: fetches its own recent
 * client tasks via the client-tasks endpoint and refreshes itself on status
 * changes, task creation and edits. Mirrors the deal dashboard Tasks widget.
 */
export const Tasks: React.FC<Props> = ({ clientId }) => {
  const { setParams } = useNavigationHooks();
  const { setCreateAction, updateTaskStatusAction } = useTaskManageActions();
  const { getClientTasksAction } = useClientActions();
  const createState = useCreateTaskSelect();

  const loadingOpts = useMemo(() => ({ defaultLoadingState: true }), []);
  const { data: tasks = [], loading, action: getClientTasks } = useActionHook(getClientTasksAction, loadingOpts);

  const filters = useMemo<IClientTaskFilters>(() => ({ recent: true }), []);

  const fetchTasks = useCallback(() => {
    getClientTasks(clientId, filters).catch((err) => {
      console.error("[Tasks] Failed to load client tasks:", err);
    });
  }, [clientId, filters, getClientTasks]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Refetch once the shared task-create modal closes (a client task may have been added).
  useDidUpdate(() => {
    if (!createState.isOpen) {
      fetchTasks();
    }
  }, [createState.isOpen]);

  const openCreate = () => setCreateAction({ isOpen: true, entity_type: "client", entity_id: clientId });

  return (
    <Stack
      sx={{
        flex: 1,
        gap: "8px",
        borderRadius: "8px",
        p: "16px",
        bgcolor: "white",
        boxShadow: "0 1px 2px 0 #1212171a",
        overflow: "hidden",
        flexGrow: 1
      }}
    >
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontSize: "18px", color: "#071F35", fontWeight: 500 }}>Tasks</Typography>
        <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
          <IconButton
            onClick={openCreate}
            size="sm"
            sx={{
              bgcolor: "#071F35",
              ".MuiSvgIcon-root": { color: "white" },
              ":hover": { bgcolor: "#071F35", ".MuiSvgIcon-root": { color: "white" } }
            }}
          >
            <AddIcon />
          </IconButton>
        </RoleComponentGuard>
      </Stack>

      <Stack sx={{ gap: "4px", overflow: "auto", flexGrow: 1 }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : tasks.length ? (
          tasks.map((item) => (
            <Stack key={item.id} direction="row" sx={{ gap: "8px", alignItems: "center" }}>
              <ActionRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                <CheckCircleOutlineIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    const next: TaskStatus =
                      item.status === "open" ? "in-progress" : item.status === "in-progress" ? "done" : "open";
                    updateTaskStatusAction({ id: item.id, status: next }).then(() => fetchTasks());
                  }}
                  sx={{ width: "20px", color: colorsStatuses[item.status], cursor: "pointer" }}
                />
              </ActionRoleGuard>
              <MaterialTooltip title={item.title}>
                <Typography
                  onClick={() => setParams({ search: { task: item.id } })}
                  sx={{
                    fontSize: "16px",
                    color: "#071F35",
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "pointer"
                  }}
                >
                  {item.title}
                </Typography>
              </MaterialTooltip>
            </Stack>
          ))
        ) : (
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
            <Stack
              onClick={openCreate}
              sx={{
                flexGrow: 1,
                p: "10px",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#F6F8FC",
                cursor: "pointer"
              }}
            >
              <ThinPlusInCircle sx={{ fontSize: "24px" }} />
              <Typography sx={{ fontSize: "15px", color: "#071F35", fontWeight: 400 }}>Add New Task</Typography>
            </Stack>
          </RoleComponentGuard>
        )}
      </Stack>

      <Task refetchTable={fetchTasks} />
    </Stack>
  );
};

export default Tasks;
